import { useRef, useState, useCallback, useEffect } from 'react';
import type { Message } from '../types';

interface UseScrollManagementOptions {
  messages: Message[];
  streamingMessage: Message | null;
  activeSessionId: string | null;
  minimized: boolean;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
}

/** Downgrade smooth scrolling to instant when the user asked for reduced motion. */
function resolveScrollBehavior(requested: ScrollBehavior): ScrollBehavior {
  return requested === 'smooth' && prefersReducedMotion() ? 'auto' : requested;
}

interface UseScrollManagementReturn {
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
  scrollToBottomVisible: boolean;
  newMessagesWhileScrolledUp: number;
  handleMessagesScroll: () => void;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
}

export function useScrollManagement({
  messages,
  streamingMessage,
  activeSessionId,
  minimized,
}: UseScrollManagementOptions): UseScrollManagementReturn {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);
  const [scrollToBottomVisible, setScrollToBottomVisible] = useState(false);
  const [newMessagesWhileScrolledUp, setNewMessagesWhileScrolledUp] = useState(0);
  const atBottomStateRef = useRef(true);
  const previousMessagesRef = useRef<{
    sessionId: string | null;
    ids: Set<string>;
  } | null>(null);

  const handleMessagesScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distance = container.scrollHeight - container.scrollTop - container.clientHeight;
    const atBottom = distance < 80;
    autoScrollRef.current = atBottom;
    if (atBottomStateRef.current !== atBottom) {
      atBottomStateRef.current = atBottom;
      setScrollToBottomVisible(!atBottom);
      if (atBottom) setNewMessagesWhileScrolledUp(0);
    }
  }, []);

  const scrollToBottom = useCallback((requestedBehavior: ScrollBehavior = 'smooth') => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const behavior = resolveScrollBehavior(requestedBehavior);
    autoScrollRef.current = true;
    atBottomStateRef.current = true;
    setScrollToBottomVisible(false);
    setNewMessagesWhileScrolledUp(0);

    const top = container.scrollHeight;
    try {
      if (typeof container.scrollTo === 'function') {
        container.scrollTo({ top, behavior });
      } else {
        container.scrollTop = top;
      }
    } catch {
      container.scrollTop = top;
    }
  }, []);

  // Auto-scroll on new messages / streaming
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !autoScrollRef.current) return;
    const behavior: ScrollBehavior = streamingMessage ? 'auto' : resolveScrollBehavior('smooth');
    const top = container.scrollHeight;
    try {
      if (typeof container.scrollTo === 'function') {
        container.scrollTo({ top, behavior });
      } else {
        container.scrollTop = top;
      }
    } catch {
      container.scrollTop = top;
    }
  }, [messages, streamingMessage]);

  // Track identities rather than length: trimming history can append a reply without
  // growing the list, while loading older history must not badge those old messages.
  useEffect(() => {
    const previous = previousMessagesRef.current;
    previousMessagesRef.current = {
      sessionId: activeSessionId,
      ids: new Set(messages.map(message => message.id)),
    };
    if (!previous || previous.sessionId !== activeSessionId || messages.length === 0) {
      autoScrollRef.current = true;
      atBottomStateRef.current = true;
      setScrollToBottomVisible(false);
      setNewMessagesWhileScrolledUp(0);

      const container = messagesContainerRef.current;
      if (container) container.scrollTop = container.scrollHeight;
      return;
    }
    if (autoScrollRef.current) return;

    let lastSharedIndex = messages.length - 1;
    while (lastSharedIndex >= 0 && !previous.ids.has(messages[lastSharedIndex].id)) {
      lastSharedIndex--;
    }
    const appended = new Set(messages.slice(lastSharedIndex + 1).map(message => message.id));
    if (appended.size > 0) {
      setNewMessagesWhileScrolledUp(n => n + appended.size);
      setScrollToBottomVisible(true);
    }
  }, [messages, activeSessionId]);

  // Reset scroll when widget opens
  useEffect(() => {
    if (minimized) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    autoScrollRef.current = true;
    atBottomStateRef.current = true;
    setScrollToBottomVisible(false);
    setNewMessagesWhileScrolledUp(0);
    container.scrollTop = container.scrollHeight;
  }, [minimized]);

  return {
    messagesEndRef,
    messagesContainerRef,
    scrollToBottomVisible,
    newMessagesWhileScrolledUp,
    handleMessagesScroll,
    scrollToBottom,
  };
}
